/**
 * The following comments tells `cat-sql-schema.js` to override column types with these.
 *
 * cat-sql-schema overrides
 * PriceRow.paymentType = PaymentType;
 * PriceRow.currency = Currency;
 * PriceRow.interval = PaymentInterval;
 * PriceRow.metadata = PriceMetadata;
 * CouponRow.currency = Currency;
 * CouponRow.duration = CouponDuration;
 * CouponRow.metadata = CouponMetadata;
 * PurchaseRow.status = PurchaseStatus;
 * PurchaseRow.metadata = PurchaseMetadata;
 * CustomerRow.metadata = GeneralMetadata;
 * ProductRow.metadata = GeneralMetadata;
 * SellerRow.metadata = GeneralMetadata;
 * LogRow.type = LogType;
 * LogRow.currency = Currency;
 * PriceRow.checkoutPageConfig = CheckoutPageConfig;
 * UpsellOfferRow.pageConfig = UpsellPageConfig;
 * UpsellOfferRow.pageConfigReturners = UpsellPageConfig;
 */

import { AccountTier } from '../types';

export type Currency =
  | 'AUD'
  | 'CAD'
  | 'EUR'
  | 'GBP'
  | 'NZD'
  | 'USD'
  | 'JPY' // Zero-decimal currency
  | 'SGD'
  | 'INR'
  | 'KRW' // Zero-decimal currency
  | 'HKD'
  | 'PHP' // Philippine Peso
  | 'CHF' // Swiss Franc
  | 'MXN' // Mexican Peso
  | 'NOK' // Norwegian Krone
  | 'ZAR';

export type PaymentType = 'free' | 'paid' | 'paymentplan' | 'subscription';

export type LogType =
  | 'purchase'
  | 'payment'
  | 'trial'
  | 'refund'
  | 'payment_failure'
  | 'subscription_canceled'
  | 'subscription_canceling'
  | 'subscription_activated'
  | 'subscriber_canceled'
  | 'seller_canceled'
  | 'payment_plan_complete';

export type PaymentInterval = 'day' | 'week' | 'month' | 'year';

export type PriceMetadata = {
  tier?: AccountTier;
  coursesToEnroll?: UUID[];
  isLifetime?: boolean;
  unlimitedInstantCourses?: boolean;
  instantCourseCredits?: number;
};

export type CouponMetadata = {
  coursesToEnroll?: UUID[];
  unlimitedInstantCourses?: boolean;
  instantCourseCredits?: number;
};

export type GeneralMetadata = Record<string, string>;

export type PurchaseMetadata = {
  offers: string;
};

export type CheckoutTheme = Record<string, string>;

export type Provider = 'none' | 'stripe';

export type CouponDuration = 'once' | 'forever' | 'repeating';

export type HtmlTemplateConfig = {
  templateMode: 'html';
  html: string;
};

export type MiraseeWelcomePageConfig = {
  headline: string;
  subtitle?: string;
  bodyHtml?: string;
  upsellBodyHtml?: string;
  ctaText?: string;
  ctaUrl?: string;
  videoUrl?: string;
  videoCaption?: string;
  calendar?: {
    title: string;
    startDate: string;
    durationMinutes: number;
    description?: string;
    recurrence?: string;
    location?: string;
  };
  smsOptin?: {
    heading?: string;
    description?: string;
    buttonText?: string;
    tag?: string;
  };
  /**
   * The "create your password" block for a brand-new account. Absent means
   * on (the historical behaviour); `enabled: false` turns it off, in which
   * case the buyer is emailed a set-password link instead. `position`:
   * 'bottom' (default) puts it at the end of the page, so the buyer reads the
   * rest first (Lizzie, 2026-10-12); 'afterBody' puts it just above the
   * body's calendar section, or after the body when there is none.
   */
  setPassword?: {
    enabled: boolean;
    position?: 'bottom' | 'afterBody';
    heading?: string;
    description?: string;
  };
};

export type MiraseeCheckoutConfig = {
  templateMode: 'mirasee-checkout';
  hero: {
    logoUrl?: string;
    headline: string;
    subtitle?: string;
  };
  priceBadgeText?: string;
  contentHtml?: string;
  pricingNote?: string;
  deadlineText?: string;
  benefits?: {
    heading?: string;
    items: Array<{ bold: string; text?: string }>;
  };
  testimonials?: Array<{
    quote: string;
    name: string;
    title?: string;
    photoUrl?: string;
  }>;
  termsHtml?: string;
  disclaimerHtml?: string;
  contactPhone?: string;
  contactEmail?: string;
  footerLogoUrl?: string;
  welcomePage?: MiraseeWelcomePageConfig;
  overrideMetadata?: { derivedFromDefaultAt: string };
};

export type SectionCheckoutPageConfig = {
  sections: CheckoutSection[];
};

export type FreeEnrollmentConfig = {
  templateMode: 'free-enrollment';
  hero: {
    headline: string;
    subtitle?: string;
    logoUrl?: string;
  };
  bodyHtml?: string;
  ctaText?: string;
  alreadyEnrolledMessage?: string;
  successHeadline?: string;
  successBodyHtml?: string;
  successCtaText?: string;
};

export type CheckoutPageConfig =
  | CheckoutSection[]
  | HtmlTemplateConfig
  | MiraseeCheckoutConfig
  | FreeEnrollmentConfig;

export function isHtmlTemplate(config: CheckoutPageConfig): config is HtmlTemplateConfig {
  return !Array.isArray(config) && 'templateMode' in config && config.templateMode === 'html';
}

export function isMiraseeCheckout(config: CheckoutPageConfig): config is MiraseeCheckoutConfig {
  return (
    !Array.isArray(config) && 'templateMode' in config && config.templateMode === 'mirasee-checkout'
  );
}

export function isFreeEnrollment(
  config: CheckoutPageConfig | undefined,
): config is FreeEnrollmentConfig {
  return (
    !!config &&
    !Array.isArray(config) &&
    'templateMode' in config &&
    config.templateMode === 'free-enrollment'
  );
}

export function isSectionArray(config: CheckoutPageConfig): config is CheckoutSection[] {
  return Array.isArray(config);
}

export type CheckoutSection =
  | HeroSection
  | ValueStackSection
  | FeatureGridSection
  | TestimonialsSection
  | VideoSection
  | ContentSection
  | TrustSection
  | FAQSection
  | CTASection
  | CheckoutFormSection
  | InstructorSection
  | UrgencySection
  | FooterSection
  | IntegratedCheckoutSection
  | ScheduleSection
  | EyebrowSection
  | CalloutSection;

type HeroSection = {
  type: 'hero';
  settings?: {
    gradientFrom?: string;
    gradientTo?: string;
    textAlign?: 'center' | 'left';
  };
  content: {
    logoUrl?: string;
    headline: string;
    subtitle?: string;
    subtitleColor?: string;
    subtitle2?: string;
    priceBadgeText?: string;
    ctaText?: string;
    ctaUrl?: string;
  };
};

type ValueStackSection = {
  type: 'valueStack';
  settings?: {
    variant?: 'checklist' | 'numbered';
    iconColor?: string;
  };
  content: {
    heading?: string;
    items: Array<{ bold: string; text?: string }>;
  };
};

type FeatureGridSection = {
  type: 'featureGrid';
  settings?: {
    columns?: 2 | 3;
  };
  content: {
    label?: string;
    heading?: string;
    items: Array<{ icon?: string; title: string; description: string }>;
  };
};

type TestimonialsSection = {
  type: 'testimonials';
  settings?: {
    variant?: 'sidebar' | 'cards' | 'inline';
  };
  content: {
    items: Array<{
      quote: string;
      name: string;
      title?: string;
      photoUrl?: string;
    }>;
  };
};

type VideoSection = {
  type: 'video';
  content: {
    url: string;
    caption?: string;
  };
};

type ContentSection = {
  type: 'content';
  settings?: {
    background?: 'white' | 'light' | 'gradient';
    maxWidth?: 'narrow' | 'wide';
  };
  content: {
    blocks: Array<{
      type: 'paragraph' | 'heading' | 'blockquote' | 'highlight' | 'benefit-list' | 'html';
      text?: string;
      html?: string;
      level?: number;
      items?: string[];
    }>;
  };
};

type TrustSection = {
  type: 'trust';
  content: {
    badges?: Array<'secure-checkout' | 'money-back' | 'stripe'>;
    guaranteeText?: string;
    phone?: string;
    altPaymentText?: string;
  };
};

type FAQSection = {
  type: 'faq';
  content: {
    heading?: string;
    items: Array<{ question: string; answer: string }>;
  };
};

type CTASection = {
  type: 'cta';
  settings?: {
    buttonColor?: string;
  };
  content: {
    acceptText: string;
    declineText: string;
  };
};

type CheckoutFormSection = {
  type: 'checkoutForm';
  settings?: {
    steps?: 1 | 3;
    layout?: 'full-width' | 'with-sidebar';
    sidebarType?: 'testimonials';
    ctaText?: string;
  };
};

type InstructorSection = {
  type: 'instructor';
  content: {
    name: string;
    title?: string;
    photoUrl?: string;
    bio: string;
  };
};

type UrgencySection = {
  type: 'urgency';
  content: {
    text: string;
  };
};

type FooterSection = {
  type: 'footer';
  content: {
    logoUrl?: string;
    disclaimerHtml?: string;
    contactPhone?: string;
    contactEmail?: string;
    termsHtml?: string;
  };
};

type IntegratedCheckoutSection = {
  type: 'integratedCheckout';
  content: {
    pricingNote?: string;
    deadlineText?: string;
    contactPhone?: string;
    contactEmail?: string;
    ctaText?: string;
    testimonials?: Array<{
      quote: string;
      name: string;
      title?: string;
      photoUrl?: string;
    }>;
  };
};

type ScheduleSection = {
  type: 'schedule';
  content: {
    heading?: string;
    items: Array<{ label: string; title: string; description: string }>;
  };
};

type EyebrowSection = {
  type: 'eyebrow';
  content: {
    text: string;
  };
};

type CalloutSection = {
  type: 'callout';
  content: {
    icon?: 'book' | 'heart' | 'star' | 'check';
    heading: string;
    description?: string;
  };
};

export type UpsellPageConfig = CheckoutSection[];

export type CampaignPageConfig = {
  sections: (CampaignSection | CheckoutSection)[];
  defaultPriceId?: string;
  alternativePriceIds?: string[];
  brand?: string;
  meta?: {
    /** Browser tab title, used exactly as entered (no site-name suffix). */
    pageTitle?: string;
    /** `<meta name="description">` served with the page. */
    description?: string;
  };
  /**
   * Show the SimpleCheck consent notice under the email field, and record
   * the submit time in Ontraport's "SimpleCheck Consent Date" field.
   */
  simpleCheckConsent?: boolean;
  ontraportTags?: string[];
  priceOntraportTags?: Record<string, string[]>;
  freeTrialDays?: number;
  deferredEnrollmentDate?: string;
  welcomePage?: MiraseeWelcomePageConfig;
  redirect?: {
    url: string;
    at: string;
  };
  /**
   * Manual override for "This launch" period start in the funnel page.
   * YYYY-MM-DD. Falls back to first tracked visit when blank.
   */
  launchDate?: string;
};

export type CampaignSection =
  | { type: 'hero'; headline: string; subtitle?: string; logoUrl?: string; badge?: string }
  | { type: 'urgency'; text: string }
  | { type: 'content'; html: string }
  | { type: 'benefits'; heading?: string; items: { bold: string; text?: string }[] }
  | {
      type: 'checkout';
      pricingNote?: string;
      deadlineText?: string;
      contactPhone?: string;
      contactEmail?: string;
      testimonials?: { quote: string; name: string; title?: string; photoUrl?: string }[];
    }
  | { type: 'faq'; heading?: string; items: { question: string; answer: string }[] }
  | { type: 'instructor'; name: string; title?: string; photoUrl?: string; bio: string }
  | {
      type: 'footer';
      logoUrl?: string;
      disclaimerHtml?: string;
      contactPhone?: string;
      contactEmail?: string;
      termsHtml?: string;
    };

export type PurchaseStatus =
  /**
   * Payment has not yet happened
   */
  | 'pending'
  /**
   * Payment has been collected, but the purchase record has not been finalized
   * and the product (course, Ruzuku account, etc) has not been provisioned yet.
   */
  | 'provisioning'
  /**
   * Checkout is complete, and the subscription is active.
   */
  | 'active'
  /**
   * Checkout is complete, and the plan or single payment succeeded and / or completed.
   */
  | 'complete'
  /**
   * The subscription / plan was canceled.
   */
  | 'canceled'
  /**
   * The subscription / plan will cancel at the end of the period.
   */
  | 'canceling'
  /**
   * The purchase has been upgraded / downgraded, and this record
   * is no longer the latest.
   */
  | 'changed'
  /**
   * The checkout process was abandoned.
   */
  | 'abandoned'
  /**
   * The provisioning process failed for some reason. Investigation and manual
   * intervation is required.
   */
  | 'failed'
  /**
   * The purchase was revoked due to going over a seat limit, or similar errors.
   */
  | 'revoked';
